<script>
/**
 * @component MemberList
 * @description ABM de afiliados: stats, búsqueda, filtros, tabla paginada,
 *              importación masiva y dialog de alta/edición con secciones agrupadas.
 * @example <MemberList />
 */
import memberService from "@/services/member";
import planService from "@/services/plan";
import patientService from "@/services/patient";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";

const DEBOUNCE_MS = 500;

// UC-REG-088: status operativo (4 valores).
// - active     → puede recibir cobertura
// - waiting    → en carencia, NO puede aún
// - inactive   → suspendido (reversible)
// - terminated → dado de baja definitiva
const STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "waiting", label: "En carencia" },
  { value: "inactive", label: "Inactivo" },
  { value: "terminated", label: "Dado de baja" },
];

// UC-REG-088: situación comercial (4 valores).
const SITUATION_OPTIONS = [
  { value: "up_to_date", label: "Al día" },
  { value: "early_default", label: "Mora temprana" },
  { value: "late_default", label: "Mora tardía" },
  { value: "suspended_unpaid", label: "Suspendido por falta de pago" },
];

const FAMILY_ROLE_OPTIONS = [
  { value: "titular", label: "Titular" },
  { value: "spouse", label: "Cónyuge" },
  { value: "child", label: "Hijo/a" },
  { value: "other", label: "Otro" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "MemberList",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterStatus: "",
      filterSituation: "",
      filterPlan: "",
      members: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      stats: {
        total: 0,
        active: 0,
        waiting: 0,
        // UC-REG-088: `inactive` ahora = suspendido reversible; `terminated`
        // = dado de baja definitivo (eran un solo bucket "inactive" antes).
        inactive: 0,
        terminated: 0,
      },
      statusOptions: STATUS_OPTIONS,
      situationOptions: SITUATION_OPTIONS,
      familyRoleOptions: FAMILY_ROLE_OPTIONS,
      // Plans
      plans: [],
      isLoadingPlans: false,
      // Dialog
      isDialogVisible: false,
      dialogMode: "create",
      editingId: null,
      form: {
        memberNumber: "",
        dni: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        plan: "",
        familyRole: "titular",
        familyGroupTitular: null,
        birthDate: "",
        gender: "",
        enrollmentDate: "",
        status: "active",
        // UC-REG-088: situación comercial + fecha de último pago.
        paymentSituation: "up_to_date",
        lastPaymentDate: "",
        address: "",
        patient: null,
      },
      formErrors: {},
      genderOptions: [
        { value: "m", label: "Masculino" },
        { value: "f", label: "Femenino" },
      ],
      // Titular search
      titularSearchInput: "",
      titularResults: [],
      isSearchingTitular: false,
      selectedTitular: null,
      // Patient search
      patientSearchInput: "",
      patientResults: [],
      isSearchingPatient: false,
      selectedPatient: null,
      // Import (UC-REG-087)
      isImportVisible: false,
      importFile: null,
      isImporting: false,
      importResult: null,
      isDraggingOver: false,
      // UC-REG-088 — Excel #2 (update status)
      isImportStatusVisible: false,
      importStatusFile: null,
      isImportingStatus: false,
      importStatusResult: null,
      isDraggingOverStatus: false,
      /** @description Indica si se está cargando el próximo número de afiliado */
      isLoadingNextNumber: false,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /** @returns {boolean} Si el rol familiar requiere vincular titular */
    needsTitular() {
      return this.form.familyRole && this.form.familyRole !== "titular";
    },
  },

  watch: {
    /**
     * @description Cuando cambia el titular seleccionado en modo creación,
     * regenera el número de afiliado como dependiente del titular.
     */
    selectedTitular(newTitular) {
      if (this.dialogMode === "create") {
        this.fetchNextNumber(newTitular ? newTitular.memberNumber : null);
      }
    },

    /**
     * @description Cuando cambia el rol familiar en modo creación,
     * regenera el número: si pasa a titular, usa secuencial nuevo; si pasa a dependiente, usa el del titular.
     */
    "form.familyRole"(newRole) {
      if (this.dialogMode === "create") {
        if (newRole === "titular") {
          this.fetchNextNumber(null);
        } else if (this.selectedTitular) {
          this.fetchNextNumber(this.selectedTitular.memberNumber);
        }
      }
    },
  },

  created() {
    this.fetchMembers();
    this.loadPlans();
  },

  methods: {
    /**
     * @description Obtiene los afiliados del backend con los filtros aplicados
     */
    async fetchMembers() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterSituation)
          params.paymentSituation = this.filterSituation;
        if (this.filterPlan) params.plan = this.filterPlan;

        const result = await memberService.query(params);
        this.members = result.docs || [];
        this.total = result.total || 0;

        this.stats = {
          total: result.total || 0,
          active: result.activeCount || 0,
          waiting: result.waitingCount || 0,
          // UC-REG-088: suspendido reversible (inactive) y baja definitiva
          // (terminated) son buckets separados.
          inactive: result.inactiveCount || 0,
          terminated: result.terminatedCount || 0,
        };
      } catch (error) {
        this.$message.error("Error al cargar afiliados");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Carga la lista de planes disponibles
     */
    async loadPlans() {
      this.isLoadingPlans = true;
      try {
        const result = await planService.query({ limit: 200 });
        this.plans = result.docs || result || [];
      } catch {
        this.plans = [];
      } finally {
        this.isLoadingPlans = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchMembers();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchMembers();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchMembers();
    },

    /**
     * @description Devuelve el label del estado
     * @param {string} status - Valor
     * @returns {string}
     */
    getStatusLabel(status) {
      const opt = STATUS_OPTIONS.find((o) => o.value === status);
      return opt ? opt.label : status || "-";
    },

    /**
     * @description Devuelve la clase badge del estado.
     *              UC-REG-088: actualizado para los 4 estados nuevos.
     * @param {string} status
     * @returns {string}
     */
    getStatusBadge(status) {
      const map = {
        active: "badge--success", // verde: puede recibir cobertura
        waiting: "badge--warning", // amarillo: en carencia
        inactive: "badge--danger", // naranja/rojo claro: suspendido reversible
        terminated: "badge--neutral", // gris: dado de baja, fuera de juego
      };
      return map[status] || "badge--neutral";
    },

    /**
     * @description UC-REG-088 — Label de la situación comercial.
     * @param {string} situation
     * @returns {string}
     */
    getSituationLabel(situation) {
      const opt = SITUATION_OPTIONS.find((o) => o.value === situation);
      return opt ? opt.label : situation || "-";
    },

    /**
     * @description UC-REG-088 — Clase del badge de situación. Up to date no
     *              tiene badge (es lo "normal", no ensucia la tabla).
     * @param {string} situation
     * @returns {string}
     */
    getSituationBadge(situation) {
      const map = {
        up_to_date: "badge--success",
        early_default: "badge--warning",
        late_default: "badge--danger",
        suspended_unpaid: "badge--neutral",
      };
      return map[situation] || "badge--neutral";
    },

    /**
     * @description Obtiene el siguiente número de afiliado disponible desde el backend
     * y lo pre-carga en el formulario. Para dependientes, recibe el número del titular.
     * @param {string|null} titularNumber - Número del titular (null = titular nuevo)
     */
    async fetchNextNumber(titularNumber = null) {
      this.isLoadingNextNumber = true;
      try {
        const { nextNumber } = await memberService.getNextNumber(
          titularNumber || undefined
        );
        this.form.memberNumber = nextNumber;
      } catch (e) {
        console.warn("No se pudo generar el número de afiliado:", e);
      } finally {
        this.isLoadingNextNumber = false;
      }
    },

    // --- Dialog ---
    openDialog(mode = "create", member = null) {
      this.dialogMode = mode;
      this.isDialogVisible = true;
      this.formErrors = {};
      this.titularSearchInput = "";
      this.titularResults = [];
      this.selectedTitular = null;
      this.patientSearchInput = "";
      this.patientResults = [];
      this.selectedPatient = null;

      if (mode === "create") {
        this.resetForm();
        this.fetchNextNumber();
        return;
      }

      if (mode === "edit" && member) {
        this.editingId = member._id;
        this.form = {
          memberNumber: member.memberNumber || "",
          dni: member.dni || "",
          firstName: member.firstName || "",
          lastName: member.lastName || "",
          email: member.email || "",
          phone: member.phone || "",
          plan:
            typeof member.plan === "object"
              ? member.plan._id
              : member.plan || "",
          familyRole: member.familyRole || member.familyGroupType || "titular",
          familyGroupTitular: member.familyGroupTitular || null,
          birthDate: member.birthDate || "",
          gender: member.gender || "",
          enrollmentDate: member.enrollmentDate || "",
          status: member.status || "active",
          // UC-REG-088: cargar situación + último pago al editar.
          paymentSituation: member.paymentSituation || "up_to_date",
          lastPaymentDate: member.lastPaymentDate || "",
          address: member.address || "",
          patient: member.patient || null,
        };
        // Restore linked titular display
        if (
          member.familyGroupTitular &&
          typeof member.familyGroupTitular === "object"
        ) {
          this.selectedTitular = member.familyGroupTitular;
        }
        // Restore linked patient display
        if (member.patient && typeof member.patient === "object") {
          this.selectedPatient = member.patient;
        }
      } else {
        this.resetForm();
      }
    },

    closeDialog() {
      this.isDialogVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.editingId = null;
      this.form = {
        memberNumber: "",
        dni: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        plan: "",
        familyRole: "titular",
        familyGroupTitular: null,
        birthDate: "",
        gender: "",
        enrollmentDate: "",
        status: "active",
        // UC-REG-088: defaults razonables para nuevo afiliado.
        paymentSituation: "up_to_date",
        lastPaymentDate: "",
        address: "",
        patient: null,
      };
      this.formErrors = {};
      this.titularSearchInput = "";
      this.titularResults = [];
      this.selectedTitular = null;
      this.patientSearchInput = "";
      this.patientResults = [];
      this.selectedPatient = null;
    },

    /**
     * @description Valida el formulario antes de guardar
     * @returns {boolean} true si es válido
     */
    validateForm() {
      this.formErrors = {};

      if (!this.form.memberNumber) {
        this.formErrors.memberNumber = "Nº de afiliado requerido";
      }
      if (!this.form.dni) {
        this.formErrors.dni = "DNI requerido";
      } else if (!/^\d{7,8}$/.test(this.form.dni)) {
        this.formErrors.dni = "DNI debe tener 7 u 8 dígitos numéricos";
      }
      if (!this.form.firstName) {
        this.formErrors.firstName = "Nombre requerido";
      }
      if (!this.form.lastName) {
        this.formErrors.lastName = "Apellido requerido";
      }
      if (this.form.email && !EMAIL_REGEX.test(this.form.email)) {
        this.formErrors.email = "Formato de email inválido";
      }

      return Object.keys(this.formErrors).length === 0;
    },

    async saveMember() {
      if (!this.validateForm()) return;

      try {
        const payload = { ...this.form };
        // Send titular ID if linked
        if (this.selectedTitular) {
          payload.familyGroupTitular = this.selectedTitular._id;
        }
        // Send patient ID if linked
        if (this.selectedPatient) {
          payload.patient = this.selectedPatient._id;
        }

        if (this.dialogMode === "create") {
          await memberService.create(payload);
          this.$message.success("Afiliado creado");
        } else {
          await memberService.update(this.editingId, payload);
          this.$message.success("Afiliado actualizado");
        }
        this.closeDialog();
        this.fetchMembers();
      } catch (error) {
        this.$message.error("Error al guardar afiliado");
      }
    },

    /**
     * @description Reenvía la invitación de acceso al afiliado (email + WhatsApp).
     * @param {Object} member - Afiliado
     * @returns {Promise<void>}
     */
    async resendInvitation(member) {
      try {
        await memberService.resendInvitation(member._id);
        this.$message.success(
          `Se reenvió la invitación a ${member.lastName}, ${member.firstName}`
        );
      } catch {
        this.$message.error("No se pudo reenviar la invitación.");
      }
    },

    async deleteMember(member) {
      try {
        await this.$confirm(
          `¿Eliminar al afiliado "${member.lastName}, ${member.firstName}"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await memberService.remove(member._id);
        this.$message.success("Afiliado eliminado");
        this.fetchMembers();
      } catch {
        // Cancelado
      }
    },

    // --- Titular search ---
    debounceTitularSearch: debounce(function () {
      this.searchTitular();
    }, DEBOUNCE_MS),

    /**
     * @description Busca afiliados titulares por nombre o nº afiliado
     */
    async searchTitular() {
      const q = this.titularSearchInput.trim();
      if (q.length < 2) {
        this.titularResults = [];
        return;
      }
      this.isSearchingTitular = true;
      try {
        const result = await memberService.query({
          searchInput: q,
          limit: 10,
        });
        this.titularResults = (result.docs || []).filter(
          (m) => m._id !== this.editingId
        );
      } catch {
        this.titularResults = [];
      } finally {
        this.isSearchingTitular = false;
      }
    },

    /**
     * @description Selecciona un titular del grupo familiar
     * @param {Object} titular - Afiliado seleccionado
     */
    selectTitular(titular) {
      this.selectedTitular = titular;
      this.form.familyGroupTitular = titular._id;
      this.titularSearchInput = "";
      this.titularResults = [];
    },

    clearTitular() {
      this.selectedTitular = null;
      this.form.familyGroupTitular = null;
    },

    // --- Patient search ---
    debouncePatientSearch: debounce(function () {
      this.searchPatient();
    }, DEBOUNCE_MS),

    /**
     * @description Busca pacientes existentes por DNI o nombre
     */
    async searchPatient() {
      const q = this.patientSearchInput.trim();
      if (q.length < 2) {
        this.patientResults = [];
        return;
      }
      this.isSearchingPatient = true;
      try {
        const result = await patientService.get({
          search: q,
          limit: 10,
        });
        this.patientResults = result.docs || result || [];
      } catch {
        this.patientResults = [];
      } finally {
        this.isSearchingPatient = false;
      }
    },

    /**
     * @description Selecciona un paciente para vinculación
     * @param {Object} patient - Paciente seleccionado
     */
    selectPatient(patient) {
      this.selectedPatient = patient;
      this.form.patient = patient._id;
      this.patientSearchInput = "";
      this.patientResults = [];
    },

    clearPatient() {
      this.selectedPatient = null;
      this.form.patient = null;
    },

    /**
     * @description Devuelve el nombre del plan. Si viene populado usa .name,
     *              si es un ObjectId string lo busca en la lista cargada.
     * @param {Object|string} plan - Plan populado o ID
     * @returns {string}
     */
    getPlanName(plan) {
      if (!plan) return "-";
      if (typeof plan === "object" && plan.name) return plan.name;
      const found = this.plans.find((p) => p._id === plan);
      return found ? found.name : "-";
    },

    /**
     * @description Devuelve nombre completo de un afiliado/paciente
     * @param {Object} m - Objeto con firstName/lastName
     * @returns {string}
     */
    getFullName(m) {
      return `${m.lastName || ""}, ${m.firstName || ""}`.trim();
    },

    // --- Import (UC-REG-087) ---

    /**
     * @description Abre el dialog de import. Resetea estado previo.
     */
    openImport() {
      this.isImportVisible = true;
      this.importFile = null;
      this.importResult = null;
      this.isDraggingOver = false;
    },

    /**
     * @description Cierra el dialog y limpia estado.
     */
    closeImport() {
      this.isImportVisible = false;
      this.importFile = null;
      this.importResult = null;
      this.isDraggingOver = false;
    },

    /**
     * @description Captura el archivo seleccionado vía input.
     * @param {Event} event - Evento del input file
     */
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.importFile = file;
        this.importResult = null;
      }
    },

    /**
     * @description Drag over: activa el highlight visual del drop zone.
     */
    onDragOver(event) {
      event.preventDefault();
      this.isDraggingOver = true;
    },

    /**
     * @description Drag leave: desactiva highlight.
     */
    onDragLeave() {
      this.isDraggingOver = false;
    },

    /**
     * @description Drop: capta el archivo soltado en la zona y lo asigna.
     */
    onDrop(event) {
      event.preventDefault();
      this.isDraggingOver = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;
      const file = files[0];
      const validExtensions = [".xlsx", ".xls", ".csv"];
      const isValid = validExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (!isValid) {
        this.$message.error(
          "Tipo de archivo no soportado. Usá .xlsx, .xls o .csv"
        );
        return;
      }
      this.importFile = file;
      this.importResult = null;
    },

    /**
     * @description Genera y descarga un Excel plantilla con los headers
     *              esperados por el endpoint /members/import + 1 fila de
     *              ejemplo. Así el admin no tiene que adivinar nombres
     *              de columnas. Hace el download client-side usando xlsx.
     */
    downloadTemplate() {
      const headers = [
        "Nº Afiliado",
        "DNI",
        "Apellido",
        "Nombre",
        "Email",
        "Teléfono",
        "Plan",
        "Grupo Familiar",
        "Fecha Alta",
        "Fecha Nacimiento",
        "Género",
        "Dirección",
      ];
      const example = [
        "100001",
        "29191617",
        "Pérez",
        "Juan",
        "juan.perez@email.com",
        "+5491123456789",
        "OSDE 210",
        "titular",
        "2026-01-15",
        "1980-05-20",
        "m",
        "Av. Siempre Viva 742",
      ];
      const ws = XLSX.utils.aoa_to_sheet([headers, example]);
      // Ancho de columnas para que se lea legible al abrirlo.
      ws["!cols"] = headers.map(() => ({ wch: 18 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Afiliados");
      XLSX.writeFile(wb, "plantilla-afiliados.xlsx");
    },

    /**
     * @description Envia el archivo al endpoint /members/import. Si hubo
     *              importados, refresca la lista. Maneja errores de validación
     *              parciales (algunos importados, algunos duplicados).
     */
    async executeImport() {
      if (!this.importFile) {
        this.$message.warning("Seleccioná un archivo primero");
        return;
      }
      this.isImporting = true;
      this.importResult = null;
      try {
        const result = await memberService.import(this.importFile);
        this.importResult = result;
        if (result.imported > 0) {
          this.$message.success(`Se importaron ${result.imported} afiliados`);
          this.fetchMembers();
        }
      } catch (error) {
        this.$message.error(error.response?.data?.error || "Error al importar");
      } finally {
        this.isImporting = false;
      }
    },

    /**
     * @description Formatea el tamaño del archivo en KB/MB para mostrar
     *              al usuario.
     * @param {number} bytes
     * @returns {string}
     */
    formatFileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    },

    // ─── UC-REG-088: Excel #2 — Update de status/situación ──────────────

    /**
     * @description Abre el dialog para actualizar status de afiliados desde
     *              Excel (sin crear, solo update).
     */
    openImportStatus() {
      this.isImportStatusVisible = true;
      this.importStatusFile = null;
      this.importStatusResult = null;
      this.isDraggingOverStatus = false;
    },

    /**
     * @description Cierra el dialog y limpia estado.
     */
    closeImportStatus() {
      this.isImportStatusVisible = false;
      this.importStatusFile = null;
      this.importStatusResult = null;
      this.isDraggingOverStatus = false;
    },

    /**
     * @description Captura el archivo seleccionado vía input para Excel #2.
     */
    onFileStatusSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.importStatusFile = file;
        this.importStatusResult = null;
      }
    },

    onDragOverStatus(event) {
      event.preventDefault();
      this.isDraggingOverStatus = true;
    },

    onDragLeaveStatus() {
      this.isDraggingOverStatus = false;
    },

    onDropStatus(event) {
      event.preventDefault();
      this.isDraggingOverStatus = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;
      const file = files[0];
      const validExtensions = [".xlsx", ".xls", ".csv"];
      const isValid = validExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (!isValid) {
        this.$message.error(
          "Tipo de archivo no soportado. Usá .xlsx, .xls o .csv"
        );
        return;
      }
      this.importStatusFile = file;
      this.importStatusResult = null;
    },

    /**
     * @description Genera y descarga la plantilla Excel #2 con headers
     *              específicos del update de status.
     */
    downloadStatusTemplate() {
      const headers = [
        "Nº Afiliado",
        "DNI",
        "Estado",
        "Situación",
        "Último Pago",
      ];
      const examples = [
        ["02000000101", "20111222", "active", "up_to_date", "2026-05-20"],
        [
          "02000000201",
          "30222333",
          "inactive",
          "suspended_unpaid",
          "2026-02-15",
        ],
        ["02000000301", "35333444", "active", "", "2026-04-10"],
      ];
      const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
      ws["!cols"] = headers.map(() => ({ wch: 18 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Actualizar estados");
      XLSX.writeFile(wb, "plantilla-actualizar-estados.xlsx");
    },

    /**
     * @description Envia el archivo al endpoint /members/import-status.
     */
    async executeImportStatus() {
      if (!this.importStatusFile) {
        this.$message.warning("Seleccioná un archivo primero");
        return;
      }
      this.isImportingStatus = true;
      this.importStatusResult = null;
      try {
        const result = await memberService.importStatus(this.importStatusFile);
        this.importStatusResult = result;
        if (result.updated > 0) {
          this.$message.success(`Se actualizaron ${result.updated} afiliados`);
          this.fetchMembers();
        } else {
          this.$message.info("No se actualizó ningún afiliado");
        }
      } catch (error) {
        this.$message.error(error.response?.data?.error || "Error al importar");
      } finally {
        this.isImportingStatus = false;
      }
    },
  },
};
</script>

<template lang="pug">
section
  //- Stats
  //- UC-REG-088: 5 cards porque ahora suspendido (reversible) y dado de baja
  //- (definitivo) son estados separados.
  .member-stats
    .member-stats__card
      .member-stats__value {{ stats.total }}
      .member-stats__label Total
    .member-stats__card.member-stats__card--success
      .member-stats__value {{ stats.active }}
      .member-stats__label Activos
    .member-stats__card.member-stats__card--warning
      .member-stats__value {{ stats.waiting }}
      .member-stats__label En carencia
    .member-stats__card.member-stats__card--danger
      .member-stats__value {{ stats.inactive }}
      .member-stats__label Suspendidos
    .member-stats__card.member-stats__card--muted
      .member-stats__value {{ stats.terminated }}
      .member-stats__label Dados de baja

  //- Filters
  header.headline
    .headline__actions
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por nombre, DNI o nº afiliado"
        :clearable="true"
        size="small"
      )
      el-select(
        v-model="filterStatus"
        placeholder="Estado"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in statusOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      //- UC-REG-088: filtro por situación comercial.
      el-select(
        v-model="filterSituation"
        placeholder="Situación"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in situationOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      //- UC-REG-087 / UC-REG-093: botones del header en una sola línea.
      //- Texto acortado ("Importar" / "Estados" / "Nuevo") para que entren
      //- en una fila sin wrap. Hover full-label via title.
      .button.button--navy-outline(
        @click="openImport()"
        v-if="isAdmin"
        title="Importar afiliados desde Excel"
      )
        micon(name="cloud_upload").button__icon
        span.button__text Importar
      .button.button--navy-outline(
        @click="openImportStatus()"
        v-if="isAdmin"
        title="Actualizar estados desde Excel"
      )
        micon(name="sync").button__icon
        span.button__text Estados
      .button.button--navy(
        @click="openDialog('create')"
        v-if="isAdmin"
        title="Nuevo Afiliado"
      )
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo

  //- Table
  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th Nº Afiliado
            th DNI
            th Nombre
            th Plan
            th Grupo Familiar
            th Estado
            //- UC-REG-088: nueva columna "Situación".
            th Situación
            th Acciones
        tbody
          spinner(v-if="isLoading")
          tr(
            v-else
            v-for="m in members"
            :key="m._id"
            @click="openDialog('edit', m)"
          )
            td {{ m.memberNumber || '-' }}
            td {{ m.dni }}
            td {{ m.lastName }}, {{ m.firstName }}
            td {{ getPlanName(m.plan) }}
            td {{ m.familyRole || m.familyGroup || '-' }}
            td
              span.badge(:class="getStatusBadge(m.status)") {{ getStatusLabel(m.status) }}
            //- UC-REG-088: si situación es up_to_date no mostramos badge
            //- (es lo "normal" — solo destacamos las anomalías).
            td
              span.badge(
                v-if="m.paymentSituation && m.paymentSituation !== 'up_to_date'"
                :class="getSituationBadge(m.paymentSituation)"
              ) {{ getSituationLabel(m.paymentSituation) }}
              span.text-muted(v-else) —
            td.actions
              .actions-container(@click.stop)
                button.button.button--action(@click="openDialog('edit', m)")
                  micon(name="edit")
                button.button.button--action(@click="resendInvitation(m)" title="Reenviar invitación")
                  micon(name="email")
                button.button.button--action(@click="deleteMember(m)" v-if="isAdmin")
                  micon(name="delete_forever")

    el-pagination(
      v-if="total > pageSize"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="onPageChange"
    )

  //- Create/Edit dialog
  el-dialog(
    :title="dialogMode === 'create' ? 'Nuevo Afiliado' : 'Editar Afiliado'"
    :visible.sync="isDialogVisible"
    width="720px"
    :close-on-click-modal="false"
  )
    .member-form

      //- ── PASO 1: TIPO DE AFILIADO ──
      .form-section
        .form-section__title Tipo de afiliado
        .form-row
          .form-field
            label.form-field__label
              span.required *
              |  ¿Es titular o dependiente?
            el-select(v-model="form.familyRole" size="small" placeholder="Seleccionar tipo")
              el-option(v-for="opt in familyRoleOptions" :key="opt.value" :label="opt.label" :value="opt.value")

        //- Si es dependiente: buscar el titular primero
        template(v-if="needsTitular")
          .form-row.form-row--1col
            .form-field
              label.form-field__label
                span.required *
                |  Titular del grupo familiar
              .linked-item(v-if="selectedTitular")
                .linked-item__info
                  span.linked-item__name {{ getFullName(selectedTitular) }}
                  span.linked-item__sub Nº {{ selectedTitular.memberNumber || '—' }} · DNI {{ selectedTitular.dni || '—' }}
                button.linked-item__clear(@click="clearTitular" type="button")
                  micon(name="close")
              template(v-else)
                el-input(
                  v-model="titularSearchInput"
                  @input="debounceTitularSearch"
                  size="small"
                  placeholder="Buscar titular por nombre o nº afiliado"
                  prefix-icon="el-icon-search"
                )
                .search-results(v-if="titularResults.length > 0")
                  .search-results__item(v-for="t in titularResults" :key="t._id" @click="selectTitular(t)")
                    span.search-results__name {{ getFullName(t) }}
                    span.search-results__sub Nº {{ t.memberNumber || '—' }} · DNI {{ t.dni || '—' }}

        //- Número de afiliado: se muestra cuando ya tenemos suficiente info para generarlo
        //- Para titular: siempre disponible. Para dependiente: solo cuando se eligió titular
        .form-row(v-if="form.familyRole === 'titular' || (needsTitular && selectedTitular)")
          .form-field
            label.form-field__label
              span.required *
              |  Nº Afiliado
            el-input(
              v-model="form.memberNumber"
              size="small"
              maxlength="20"
              :placeholder="isLoadingNextNumber ? 'Generando...' : ''"
              :readonly="dialogMode === 'create' && form.familyRole === 'titular'"
              :class="{ 'input-readonly': dialogMode === 'create' && form.familyRole === 'titular' }"
            )
            span.form-field__hint(v-if="form.familyRole === 'titular'") Generado automáticamente
            span.form-field__hint(v-else) Sugerido según el titular · podés ajustarlo
            span.has-error(v-if="formErrors.memberNumber") {{ formErrors.memberNumber }}

      //- ── DATOS PERSONALES ──
      .form-section
        .form-section__title Datos personales
        .form-row
          .form-field
            label.form-field__label
              span.required *
              |  DNI
            el-input(v-model="form.dni" size="small" maxlength="8" placeholder="Ej: 30123456")
            span.has-error(v-if="formErrors.dni") {{ formErrors.dni }}
        .form-row
          .form-field
            label.form-field__label
              span.required *
              |  Nombre
            el-input(v-model="form.firstName" size="small" maxlength="50" placeholder="Ej: Juan")
            span.has-error(v-if="formErrors.firstName") {{ formErrors.firstName }}
          .form-field
            label.form-field__label
              span.required *
              |  Apellido
            el-input(v-model="form.lastName" size="small" maxlength="50" placeholder="Ej: Pérez")
            span.has-error(v-if="formErrors.lastName") {{ formErrors.lastName }}
        .form-row
          .form-field
            label.form-field__label Fecha de nacimiento
            el-date-picker(v-model="form.birthDate" type="date" size="small" placeholder="Seleccionar fecha")
          .form-field
            label.form-field__label Género
            el-select(v-model="form.gender" size="small" placeholder="Seleccionar género" clearable)
              el-option(v-for="opt in genderOptions" :key="opt.value" :label="opt.label" :value="opt.value")

      //- ── CONTACTO ──
      .form-section
        .form-section__title Contacto
        .form-row
          .form-field
            label.form-field__label Email
            el-input(v-model="form.email" size="small" maxlength="100" placeholder="Ej: juan@email.com")
            span.has-error(v-if="formErrors.email") {{ formErrors.email }}
          .form-field
            label.form-field__label Teléfono
            el-input(v-model="form.phone" size="small" maxlength="20" placeholder="Ej: 11-1234-5678")
        .form-row.form-row--1col
          .form-field
            label.form-field__label Dirección
            el-input(v-model="form.address" size="small" maxlength="200" placeholder="Ej: Av. Corrientes 1234, CABA")

      //- ── COBERTURA ──
      .form-section
        .form-section__title Cobertura
        .form-row
          .form-field
            label.form-field__label Plan
            el-select(v-model="form.plan" size="small" placeholder="Seleccionar plan" clearable filterable :loading="isLoadingPlans")
              el-option(v-for="p in plans" :key="p._id" :label="p.name" :value="p._id")
          .form-field
            label.form-field__label Estado
            el-select(v-model="form.status" size="small" placeholder="Seleccionar estado")
              el-option(v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value")
        //- UC-REG-088: situación comercial + último pago.
        //- Si vienen ambos, el backend respeta la situación explícita (override).
        //- Si solo viene "Último Pago", el backend recalcula situación con las
        //- reglas configuradas a nivel org (Administración → Reglas de afiliación).
        .form-row
          .form-field
            label.form-field__label Situación
            el-select(v-model="form.paymentSituation" size="small" placeholder="Seleccionar situación")
              el-option(v-for="opt in situationOptions" :key="opt.value" :label="opt.label" :value="opt.value")
          .form-field
            label.form-field__label Último pago
            el-date-picker(v-model="form.lastPaymentDate" type="date" size="small" placeholder="Seleccionar fecha" style="width: 100%")
        .form-row
          .form-field
            label.form-field__label Fecha de alta
            el-date-picker(v-model="form.enrollmentDate" type="date" size="small" placeholder="Seleccionar fecha")

      //- ── VINCULACIÓN CON PACIENTE ──
      .form-section
        .form-section__title Vinculación con paciente
        .form-row.form-row--1col
          .form-field
            label.form-field__label Paciente vinculado
            .linked-item(v-if="selectedPatient")
              .linked-item__info
                span.linked-item__name {{ getFullName(selectedPatient) }}
                span.linked-item__sub DNI {{ selectedPatient.dni || selectedPatient.documentNumber || '—' }}
              button.linked-item__clear(@click="clearPatient" type="button")
                micon(name="close")
            template(v-else)
              el-input(
                v-model="patientSearchInput"
                @input="debouncePatientSearch"
                size="small"
                placeholder="Buscar paciente por DNI o nombre"
                prefix-icon="el-icon-search"
              )
              .search-results(v-if="patientResults.length > 0")
                .search-results__item(v-for="p in patientResults" :key="p._id" @click="selectPatient(p)")
                  span.search-results__name {{ getFullName(p) }}
                  span.search-results__sub DNI {{ p.dni || p.documentNumber || '—' }}
            p.form-hint Opcional. Vincular con un paciente existente del sistema clínico.

    div(slot="footer")
      el-button(@click="closeDialog" size="small") Cancelar
      el-button(type="primary" @click="saveMember" size="small") Guardar

  //- ── Import dialog (UC-REG-087) ─────────────────────────────────────
  //- Rediseño completo: card de instrucciones + descarga de plantilla,
  //- drop zone con drag&drop, resultado detallado con duplicados y
  //- errores expandibles.
  el-dialog(
    title="Importar Afiliados desde Excel"
    :visible.sync="isImportVisible"
    width="640px"
    :before-close="closeImport"
  )
    .import-wrapper
      //- Instrucciones + plantilla
      .import-info
        .import-info__header
          h4 Formato del archivo
          button.import-template-btn(@click="downloadTemplate")
            micon(name="cloud_download")
            span Descargar plantilla
        p.import-info__lead
          | Subí un archivo
          strong  .xlsx, .xls o .csv
          | con las siguientes columnas:
        .import-columns
          .import-columns__group
            span.import-columns__label Obligatorias
            .import-columns__pills
              span.pill.pill--required Nº Afiliado
              span.pill.pill--required DNI
              span.pill.pill--required Apellido
              span.pill.pill--required Nombre
          .import-columns__group
            span.import-columns__label Opcionales
            .import-columns__pills
              span.pill Email
              span.pill Teléfono
              span.pill Plan
              span.pill Grupo Familiar
              span.pill Fecha Alta
              span.pill Fecha Nacimiento
              span.pill Género
              span.pill Dirección

      //- Drop zone
      .import-dropzone(
        :class="{ 'is-dragging': isDraggingOver, 'has-file': !!importFile }"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      )
        input.import-file-input(
          ref="importFileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          @change="onFileSelected"
        )
        .import-dropzone__inner(v-if="!importFile")
          micon.import-dropzone__icon(name="cloud_upload")
          p.import-dropzone__primary
            strong Arrastrá el archivo acá
            |  o
            a.import-dropzone__browse(@click.prevent="$refs.importFileInput.click()")  hacé click para seleccionar
          p.import-dropzone__hint Acepta .xlsx, .xls o .csv (máx. 5 MB)
        .import-dropzone__file(v-else)
          micon.import-dropzone__file-icon(name="description")
          .import-dropzone__file-info
            p.import-dropzone__file-name {{ importFile.name }}
            p.import-dropzone__file-meta {{ formatFileSize(importFile.size) }}
          button.import-dropzone__remove(@click="importFile = null; importResult = null") ×

      //- Resultado
      .import-result(v-if="importResult")
        .import-result__summary(:class="importResult.imported === importResult.total ? 'is-success' : 'is-partial'")
          .import-result__metric
            span.metric-value {{ importResult.imported }}
            span.metric-label Importados
          .import-result__metric(v-if="importResult.duplicates > 0")
            span.metric-value.is-warning {{ importResult.duplicates }}
            span.metric-label Duplicados
          .import-result__metric(v-if="importResult.errors && importResult.errors.length")
            span.metric-value.is-error {{ importResult.errors.length }}
            span.metric-label Errores
          .import-result__metric
            span.metric-value.is-total {{ importResult.total }}
            span.metric-label Total filas
        .import-result__errors(v-if="importResult.errors && importResult.errors.length")
          details
            summary {{ importResult.errors.length }} errores en la importación (click para ver)
            ul
              li(v-for="(err, idx) in importResult.errors.slice(0, 20)" :key="idx") {{ err }}
              li(v-if="importResult.errors.length > 20")
                em ... y {{ importResult.errors.length - 20 }} más

    div(slot="footer")
      el-button(@click="closeImport") Cerrar
      el-button(
        type="primary"
        @click="executeImport"
        :loading="isImporting"
        :disabled="!importFile"
      ) {{ isImporting ? 'Importando...' : 'Importar' }}

  //- ── UC-REG-088: Excel #2 — Actualizar estados ──────────────────────
  //- Mismo patrón visual que el Excel #1 (drop zone + plantilla + result),
  //- pero los headers de la plantilla son específicos para update de status:
  //- Nº Afiliado, DNI, Estado, Situación, Último Pago.
  //- Importante: si el afiliado no existe en la org, va a errores (no crea).
  el-dialog(
    title="Actualizar estados de afiliación"
    :visible.sync="isImportStatusVisible"
    width="640px"
    :before-close="closeImportStatus"
  )
    .import-wrapper
      .import-info
        .import-info__header
          h4 Formato del archivo
          button.import-template-btn(@click="downloadStatusTemplate")
            micon(name="cloud_download")
            span Descargar plantilla
        p.import-info__lead
          | Subí un archivo
          strong  .xlsx, .xls o .csv
          | con las siguientes columnas:
        .import-columns
          .import-columns__group
            span.import-columns__label Identificador
            .import-columns__pills
              span.pill.pill--required Nº Afiliado o DNI
          .import-columns__group
            span.import-columns__label Datos a actualizar
            .import-columns__pills
              span.pill Estado
              span.pill Situación
              span.pill Último Pago
        p.import-info__lead(style="margin-top:8px;font-size:11px")
          | Si tipeás solo "Último Pago", la situación se calcula con las reglas configuradas en
          strong  Administración → Reglas de afiliación.
          |  Si el afiliado no existe → fila va a errores (NO se crea).

      .import-dropzone(
        :class="{ 'is-dragging': isDraggingOverStatus, 'has-file': !!importStatusFile }"
        @dragover="onDragOverStatus"
        @dragleave="onDragLeaveStatus"
        @drop="onDropStatus"
      )
        input.import-file-input(
          ref="importStatusFileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          @change="onFileStatusSelected"
        )
        .import-dropzone__inner(v-if="!importStatusFile")
          micon.import-dropzone__icon(name="cloud_upload")
          p.import-dropzone__primary
            strong Arrastrá el archivo acá
            |  o
            a.import-dropzone__browse(@click.prevent="$refs.importStatusFileInput.click()")  hacé click para seleccionar
          p.import-dropzone__hint Acepta .xlsx, .xls o .csv (máx. 5 MB)
        .import-dropzone__file(v-else)
          micon.import-dropzone__file-icon(name="description")
          .import-dropzone__file-info
            p.import-dropzone__file-name {{ importStatusFile.name }}
            p.import-dropzone__file-meta {{ formatFileSize(importStatusFile.size) }}
          button.import-dropzone__remove(@click="importStatusFile = null; importStatusResult = null") ×

      .import-result(v-if="importStatusResult")
        .import-result__summary(:class="importStatusResult.notFound === 0 && (importStatusResult.errors || []).length === 0 ? 'is-success' : 'is-partial'")
          .import-result__metric
            span.metric-value {{ importStatusResult.updated }}
            span.metric-label Actualizados
          .import-result__metric(v-if="importStatusResult.notFound > 0")
            span.metric-value.is-warning {{ importStatusResult.notFound }}
            span.metric-label No encontrados
          .import-result__metric(v-if="importStatusResult.errors && importStatusResult.errors.length")
            span.metric-value.is-error {{ importStatusResult.errors.length }}
            span.metric-label Errores
          .import-result__metric
            span.metric-value.is-total {{ importStatusResult.total }}
            span.metric-label Total filas
        .import-result__errors(v-if="importStatusResult.errors && importStatusResult.errors.length")
          details
            summary {{ importStatusResult.errors.length }} errores / no encontrados (click para ver)
            ul
              li(v-for="(err, idx) in importStatusResult.errors.slice(0, 20)" :key="idx") {{ err }}
              li(v-if="importStatusResult.errors.length > 20")
                em ... y {{ importStatusResult.errors.length - 20 }} más

    div(slot="footer")
      el-button(@click="closeImportStatus") Cerrar
      el-button(
        type="primary"
        @click="executeImportStatus"
        :loading="isImportingStatus"
        :disabled="!importStatusFile"
      ) {{ isImportingStatus ? 'Actualizando...' : 'Actualizar' }}
</template>

<style lang="scss" scoped>
.member-stats {
  display: grid;
  // UC-REG-088: 5 cards (Total, Activos, Carencia, Suspendidos, Bajas).
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  &__card {
    background: #fff;
    border: 1px solid $border-color;
    border-radius: 12px;
    padding: 16px 20px;
    text-align: center;

    &--success {
      border-left: 3px solid #22c55e;
    }
    &--warning {
      border-left: 3px solid #f59e0b;
    }
    &--danger {
      border-left: 3px solid #ef4444;
    }
    // UC-REG-088: bajas definitivas — gris para visualmente distinguir de
    // "suspendido" (rojo, reversible).
    &--muted {
      border-left: 3px solid #94a3b8;
      opacity: 0.85;
    }
  }

  &__value {
    font-size: 24px;
    font-weight: 800;
    color: $text-primary-new;
  }

  &__label {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 2px;
  }
}

// UC-REG-088 — header de Afiliados: el `.button` global rinde 48px y los
// `el-input size="small"` 32px, por lo que la barra de filtros queda
// desalineada (botones altos, buscadores chiquitos). Normalizamos todo a
// 36px y centramos texto/icono dentro del botón.
.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;

    .button__icon {
      display: inline-flex;
      align-items: center;
      width: 16px;
      height: 16px;
    }
  }

  :deep(.el-input__inner),
  :deep(.el-input--small .el-input__inner) {
    height: 36px;
    line-height: 36px;
  }

  :deep(.el-input__icon) {
    line-height: 36px;
  }
}

// UC-REG-093: anchos compactos para que header entre en una línea hasta
// ~1280px de viewport. Por debajo, flex-wrap del .headline__actions hace
// que pase a multilínea sin romper layout.
.search__input {
  width: 240px;
}
.el-select {
  width: 140px;
}

// ─── Member form (dialog) ───
.member-form {
  padding: 0 4px;
}

.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  background: white;

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin-bottom: 16px;
  }
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 12px;

  &--1col {
    grid-template-columns: 1fr;
  }
}

.form-field {
  min-width: 0;

  .el-select,
  .el-date-editor {
    width: 100%;
  }
}

.form-field__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 4px;

  .required {
    color: #ef4444;
    margin-right: 2px;
  }
}

.has-error {
  color: #ef4444;
  font-size: 12px;
}

.form-field__hint {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
}

.input-readonly {
  :deep(.el-input__inner) {
    background: #f8fafc;
    color: #475569;
    cursor: default;
    font-family: monospace;
    letter-spacing: 0.04em;
  }
}

// ─── Search results dropdown ───
.search-results {
  border: 1px solid $border-color;
  border-radius: 8px;
  background: #fff;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &__item {
    display: flex;
    flex-direction: column;
    padding: 8px 12px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #f8fafc;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #f1f5f9;
    }
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: $text-primary-new;
  }

  &__sub {
    font-size: 11px;
    color: $text-secondary-new;
  }
}

// ─── Linked item chip ───
.linked-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid $border-color;
  border-radius: 8px;

  &__info {
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: $text-primary-new;
  }

  &__sub {
    font-size: 11px;
    color: $text-secondary-new;
  }

  &__clear {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    .micon {
      width: 16px;
      height: 16px;
      fill: $text-muted-new;
      transition: fill 0.15s ease;
    }

    &:hover .micon {
      fill: #ef4444;
    }
  }
}

.form-hint {
  font-size: 11px;
  color: $text-muted-new;
  margin: 4px 0 0;
}

.file-input {
  width: 100%;
  padding: 8px 0;
}
.import-form {
  padding: 0 5px;
}

// UC-REG-087: rediseño del dialog de import de afiliados.
.import-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 6px;
}

.import-info {
  background: #f8fafc;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 14px 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    h4 {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
      color: $text-primary-new;
    }
  }

  &__lead {
    margin: 0 0 12px;
    font-size: 12px;
    color: $text-secondary-new;

    strong {
      color: $text-primary-new;
    }
  }
}

.import-template-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: white;
  border: 1px solid $border-color;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--org-color-primary, $navy);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--org-color-primary, $navy);
    background: #f1f5f9;
  }

  .micon {
    width: 14px;
    height: 14px;
    fill: var(--org-color-primary, $navy);
  }
}

.import-columns {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $text-muted-new;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    min-width: 80px;
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
}

.pill {
  display: inline-block;
  background: white;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 2px 9px;
  font-size: 10.5px;
  color: $text-secondary-new;

  &--required {
    background: var(--org-color-primary, $navy);
    color: white;
    border-color: transparent;
  }
}

.import-dropzone {
  position: relative;
  border: 2px dashed $border-color;
  border-radius: 12px;
  padding: 28px 20px;
  background: #fafbfc;
  transition: all 0.18s ease;
  cursor: pointer;

  &.is-dragging {
    border-color: var(--org-color-primary, $navy);
    background: #eff6ff;
  }

  &.has-file {
    border-style: solid;
    border-color: var(--org-color-primary, $navy);
    background: white;
    cursor: default;
  }

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
  }

  &__icon {
    width: 38px;
    height: 38px;
    fill: $text-muted-new;
    opacity: 0.65;
  }

  &__primary {
    margin: 0;
    font-size: 13px;
    color: $text-secondary-new;

    strong {
      color: $text-primary-new;
    }
  }

  &__browse {
    color: var(--org-color-primary, $navy);
    cursor: pointer;
    font-weight: 600;
    text-decoration: underline;
  }

  &__hint {
    margin: 0;
    font-size: 11px;
    color: $text-muted-new;
  }

  &__file {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__file-icon {
    width: 32px;
    height: 32px;
    fill: var(--org-color-primary, $navy);
    flex-shrink: 0;
  }

  &__file-info {
    flex: 1;
    min-width: 0;

    p {
      margin: 0;
    }
  }

  &__file-name {
    font-size: 13px;
    font-weight: 600;
    color: $text-primary-new;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__file-meta {
    font-size: 11px;
    color: $text-muted-new;
    margin-top: 2px !important;
  }

  &__remove {
    border: none;
    background: transparent;
    color: $text-muted-new;
    font-size: 22px;
    cursor: pointer;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 4px;

    &:hover {
      color: #ef4444;
      background: #fef2f2;
    }
  }
}

.import-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;

  // Cuando ya hay file, ocultamos el input para que no atrape clicks.
  .has-file & {
    display: none;
  }
}

.import-result {
  margin-top: 4px;

  &__summary {
    display: flex;
    justify-content: space-around;
    gap: 12px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 10px;
    padding: 14px 12px;

    &.is-partial {
      background: #fffbeb;
      border-color: #fde68a;
    }
  }

  &__metric {
    display: flex;
    flex-direction: column;
    align-items: center;

    .metric-value {
      font-size: 20px;
      font-weight: 800;
      color: $text-primary-new;
      line-height: 1.1;

      &.is-warning {
        color: #d97706;
      }
      &.is-error {
        color: #dc2626;
      }
      &.is-total {
        color: $text-muted-new;
      }
    }

    .metric-label {
      font-size: 10.5px;
      color: $text-secondary-new;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 2px;
    }
  }

  &__errors {
    margin-top: 10px;
    font-size: 12px;

    details summary {
      cursor: pointer;
      color: #dc2626;
      font-weight: 600;
      padding: 6px 0;
    }

    ul {
      margin: 6px 0 0;
      padding-left: 20px;
      color: $text-secondary-new;
      max-height: 160px;
      overflow-y: auto;
    }
  }
}

// UC-REG-087: variante "navy outline" del botón. Mismo footprint que
// --navy pero invertido (fondo blanco, border navy). Da consistencia
// visual con el botón principal "Nuevo Afiliado" sin competir por
// atención.
.button--navy-outline {
  background: white;
  color: var(--org-color-primary, $navy);
  border: 1px solid var(--org-color-primary, $navy);
  border-radius: 8px;

  &:hover {
    background: rgba(0, 45, 94, 0.05);
  }

  .micon {
    fill: var(--org-color-primary, $navy);
  }
}

tbody td:hover {
  cursor: pointer;
}
</style>
